// Panel MPC56xx — baca/hapus/program lewat TRMD JTAG lokal (ws://localhost:8767).
// Urutan aman: CONNECT -> READ (dump dulu) -> ERASE -> PROGRAM + verifikasi baca-balik.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { isSensitiveLog } from "../flashtools/lib/consoleFilter";
import { Cpu, ClipboardCopy, Eraser, HardDriveDownload, Plug, Upload, ScanSearch, Cable } from "lucide-react";
import { toast } from "sonner";
import {
  JLINK_TARGETS, pingJLinkEngine,
  jlinkConnect, jlinkRead, jlinkErase, jlinkProgram, jlinkVerify, jlinkReadMulti,
  mpcShadowRestore, bamPorts, bamProbe,
} from "../flashtools/lib/jlinkEngine";
import { saveBinaryWithPicker } from "../flashtools/lib/saveAs";
import { SaveAsDialog } from "../flashtools/components/SaveAsDialog";

// Panduan RST melayang di tengah layar — pola sama dengan cold guide Honda Flashtools.
const RstGuide = ({ phase, seconds }) => (
  <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[min(92vw,520px)] animate-in fade-in zoom-in-95 duration-200"
    data-testid="jlink-rst-guide">
    <div className={`rounded-xl border-2 ${phase === "detach" ? "border-amber-400/60 bg-[#1a1206]/95" : "border-emerald-400/60 bg-[#06160f]/95"} backdrop-blur-md shadow-2xl shadow-black/60 px-5 py-4 flex items-center gap-4`}>
      <Cable className={phase === "detach" ? "text-amber-300" : "text-emerald-300"} size={30} />
      <div className="flex-1">
        <div className={`font-black text-[18px] ${phase === "detach" ? "text-amber-300" : "text-emerald-300"}`}>
          {phase === "detach" ? "DISCONNECT RST CABLE NOW" : "CONNECT RST CABLE NOW"}
        </div>
        <div className="text-xs text-zinc-400 mt-0.5">
          {phase === "detach" ? "CONNECT phase — target detected without active reset" : "HALT phase — core halted, reading memory"}
        </div>
      </div>
      {seconds > 0 && <span className="text-4xl font-mono font-bold text-zinc-100" data-testid="jlink-rst-guide-sec">{seconds}s</span>}
    </div>
  </div>
);

const BTN = "h-[38px] rounded-[5px] inline-flex items-center justify-center gap-2 text-[12px] font-semibold tracking-wide border transition-colors disabled:opacity-35 disabled:cursor-not-allowed";
const BTN_ACCENT = `${BTN} border-[#2b5f8f] bg-[#16293c] text-sky-200 hover:bg-[#1b3449] hover:border-[#3a79b0]`;
const BTN_NEUTRAL = `${BTN} border-[#262c36] bg-[#161a21] text-zinc-300 hover:bg-[#1d2027] hover:border-[#323a46]`;
const BTN_DANGER = `${BTN} border-[#5a2a24] bg-[#20110f] text-[#d98b7f] hover:bg-[#2a1613] hover:border-[#73362d]`;

// MPC56xx JTAG runs on the SEGGER J-Link Windows engine (TRMD JTAG, ws://localhost:8767) — no Android J-Link library exists.
const IS_ANDROID_APP = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "") && /TRMDPerformance|TiroTunerApp/i.test(navigator.userAgent || "");

const hx = (n, w = 8) => "0x" + (n >>> 0).toString(16).toUpperCase().padStart(w, "0");

export default function Mpc5602dFlash({ embedded = false, onBusyChange }) {
  const [engine, setEngine] = useState({ online: false });
  const [tIdx, setTIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("siap");
  const [lines, setLines] = useState([]);
  const [dump, setDump] = useState(null);
  const [saveAsReq, setSaveAsReq] = useState(null);
  const [copied, setCopied] = useState(false);
  const [file, setFile] = useState(null);
  const [rstPause] = useState(0);
  const [guide, setGuide] = useState(null);
  const [rstAsk, setRstAsk] = useState(null);
  const [readInfo, setReadInfo] = useState(null);
  const [haltToast, setHaltToast] = useState(false);
  const [connected, setConnected] = useState(false);
  const [rstGuide, setRstGuide] = useState({ open: false, phase: "", seconds: 0 });
  const [readRegionKey, setReadRegionKey] = useState("code-flash");
  const [writeRegionKey, setWriteRegionKey] = useState("code-flash");
  const logRef = useRef(null);
  const pingedRef = useRef(false);
  const programInputRef = useRef(null);
  const [fullOpen, setFullOpen] = useState(false);
  const [fullFiles, setFullFiles] = useState({});
  const tickRef = useRef(null);
  const target = JLINK_TARGETS[tIdx];

  // Notifikasi melayang: satu toast saja (ID tetap), supaya tidak menumpuk.
  const log = useCallback((kind, msg) => {
    if (isSensitiveLog(msg)) return;
    const t = new Date().toLocaleTimeString("en-GB", { hour12: false });
    setLines((p) => [...p.slice(-400), { t, kind, msg }]);
    if (kind === "ERROR") toast.error(msg, { id: "mcu" });
    else if (kind === "WARN") toast.warning(msg, { id: "mcu" });
    else if (kind === "SUCCESS" || kind === "WRITE") toast.success(msg, { id: "mcu" });
  }, []);

  const closeGuide = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setRstGuide({ open: false, phase: "", seconds: 0 });
  }, []);

  const openGuide = useCallback((phase) => {
    const total = Math.round(rstPause / 1000);
    setRstGuide({ open: true, phase, seconds: total });
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setRstGuide((g) => {
        if (!g.open) return g;
        if (g.seconds <= 1) { clearInterval(tickRef.current); tickRef.current = null; return { open: false, phase: "", seconds: 0 }; }
        return { ...g, seconds: g.seconds - 1 };
      });
    }, 1000);
  }, [rstPause]);

  useEffect(() => () => { if (tickRef.current) clearInterval(tickRef.current); }, []);

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [lines]);

  const checkEngine = useCallback(async () => {
    const r = await pingJLinkEngine();
    setEngine(r);
    if (r.online) setLines((p) => [...p.slice(-400), { t: new Date().toLocaleTimeString("en-GB", { hour12: false }), kind: "INFO", msg: `Engine online (v${r.version || "?"})` }]);
    else log("ERROR", "TRMD Engine not detected — run TRMDEngine.exe.");
    return r;
  }, [log]);

  useEffect(() => { setConnected(false); }, [tIdx]);

  useEffect(() => { onBusyChange?.(busy); }, [busy, onBusyChange]);

  // Notif proses selalu tampil (tanpa auto-hide) sampai user klik CLOSE.
  useEffect(() => { if (readInfo) setHaltToast(true); }, [readInfo]);

  useEffect(() => { if (!pingedRef.current) { pingedRef.current = true; checkEngine(); } }, [checkEngine]);

  const onPhase = useCallback((m) => {
    if (m && m.msg) {
      const s = String(m.msg);
      if (s.indexOf("LEPAS kabel RST") >= 0) openGuide("detach");
      else if (s.indexOf("SAMBUNGKAN kabel RST") >= 0) openGuide("attach");
      // Saat engine mulai reset+halt, target butuh RST aktif kembali.
      if (s.indexOf("reset+halt percobaan") >= 0 || s.indexOf("Failed to halt") >= 0) { disarmConnectGuide(); setGuide("on"); }
      else if (s.indexOf("memori TERBACA") >= 0 || s.indexOf("attach ✓") >= 0) {
        disarmConnectGuide(); setGuide(null);
        if (s.indexOf("ter-halt") >= 0 || s.indexOf("memori TERBACA") >= 0) setReadInfo({ addr: "siap", pct: 0, halted: true });
      }
      else if (s.indexOf("devices:") >= 0 || s.indexOf("OnCE-TAP") >= 0) disarmConnectGuide();
      const m2 = s.match(/(baca|tulis) (0x[0-9A-F]{8})(?: → (0x[0-9A-F]{8}))? · (\d+)%/);
      if (m2) setReadInfo((p) => ({ ...(p || {}), addr: m2[3] ? `${m2[2]} → ${m2[3]}` : m2[2], pct: (p && p.pct) || Number(m2[4]), halted: true, mode: m2[1] }));
      const m3 = s.match(/Region (0x[0-9A-F]{8}) · (\d+) byte/);
      if (m3) setReadInfo((p) => ({ ...(p || {}), addr: `${m3[1]} · ${m3[2]} B`, halted: true }));
      if (s.indexOf("DLL:") === 0) return;
      log("INFO", s);
    }
    if (m && m.progress != null && m.total) {
      const pct = Math.round((m.progress / m.total) * 100);
      setProgress(pct); setStatus(`${pct}%`);
      setReadInfo((p) => (p && !p.done ? { ...p, pct } : p));
    }
  }, [log, openGuide]);

  const logText = useCallback(() => lines.map((l) => `[${l.t}] ${l.msg}`).join("\n"), [lines]);

  const copyLog = useCallback(async () => {
    const txt = logText();
    try { await navigator.clipboard.writeText(txt); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = txt; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } finally { document.body.removeChild(ta); }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }, [logText]);

  const opts = useCallback(() => ({ dll: "", device: target.id, rstPause }), [target, rstPause]);

  const regionList = target.regions || [{ name: "code-flash", base: target.base, size: target.size }];
  const readRegion = regionList.find((r) => r.name === readRegionKey) || regionList[0];
  const writeRegion = regionList.find((r) => r.name === writeRegionKey) || regionList[0];
  const regionLabel = (r) => `${r.name} · ${hx(r.base)} · ${(r.size / 1024).toFixed(0)} KB`;

  const run = useCallback(async (label, fn) => {
    const r = await checkEngine();
    if (!r.online) return null;
    setBusy(true); setProgress(0); setStatus(label);
    const quiet = label === "CONNECT";
    if (!quiet) setReadInfo({ addr: "menyambung ke target…", pct: 0, halted: false, job: label });
    try {
      const res = await fn();
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      setStatus(`${label} complete`);
      closeGuide();
      setGuide(null);
      if (quiet) setReadInfo(null);
      else setReadInfo((p) => ({ ...(p || {}), pct: 100, halted: true, done: true, ok: true, result: `${label} SELESAI ✓` }));
      return res;
    } catch (e) {
      log("ERROR", `${label} failed: ${e.message || e}`);
      setStatus(`${label} GAGAL`);
      closeGuide();
      setGuide(null);
      if (quiet) setReadInfo(null);
      else setReadInfo((p) => ({ ...(p || {}), halted: true, done: true, ok: false, result: `${label} GAGAL — ${e.message || e}` }));
      return null;
    } finally { setBusy(false); setProgress(0); }
  }, [checkEngine, log, closeGuide]);

  // Aba-aba RST: muncul melayang saat CONNECT (tombol reset harus OFF) dan sebelum READ.
  // Notif "OFF BUTTON RESET" hanya muncul kalau koneksi belum dapat dalam beberapa detik
  // (artinya tombol reset kemungkinan belum OFF). Kalau sudah OFF, connect cepat → notif dilewati.
  const guideTimer = useRef(null);
  const attachedRef = useRef(false);
  const armConnectGuide = useCallback(() => {
    attachedRef.current = false;
    if (guideTimer.current) clearTimeout(guideTimer.current);
    guideTimer.current = setTimeout(() => {
      if (!attachedRef.current) setGuide("connect");
    }, 2500);
  }, []);
  const disarmConnectGuide = useCallback(() => {
    attachedRef.current = true;
    if (guideTimer.current) { clearTimeout(guideTimer.current); guideTimer.current = null; }
  }, []);

  const askRst = useCallback((mode) => new Promise((resolve) => setRstAsk({ mode, resolve })), []);

  // --- Pemulihan censorship -------------------------------------------------
  const doBamProbe = useCallback(async () => {
    const r = await checkEngine();
    if (!r.online) return;
    const pl = await bamPorts();
    const ports = (pl && pl.ports) || [];
    if (!ports.length) { log("WARN", "No COM port detected. Connect the 3.3 V USB-TTL adapter first."); return; }
    log("INFO", `COM ports detected: ${ports.join(", ")}`);
    const port = ports.length === 1 ? ports[0] : window.prompt(`Select COM port (${ports.join(", ")})`, ports[0]);
    if (!port) return;
    log("WARN", "Set boot pins to SERIAL BOOT (FAB/BOOTCFG), reset the ECU, then click OK.");
    await run("CEK BAM", () => bamProbe(port, onPhase));
  }, [checkEngine, log, run]);

  const doShadowRestore = useCallback(async (bytes) => {
    const txt = bytes
      ? `Rewrite the ENTIRE shadow block (${bytes.length} B) from the backup file?`
      : "Rewrite default censorship words (NVPWD 0xFEEDFACE/0xCAFEBEEF, NVSCI 0x55AA55AA)?";
    if (!window.confirm(`${txt}\n\nThe shadow block will be ERASED first. Continue?`)) return;
    log("WRITE", "SHADOW RECOVERY — do not disconnect cable or power until finished.");
    const res = await run("PULIHKAN SHADOW", () => mpcShadowRestore(opts(), bytes, onPhase));
    if (res && res.uncensored) toast.success("Shadow restored — power-cycle the ECU");
  }, [log, run, opts]);

  const onShadowFile = useCallback(async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    const bytes = new Uint8Array(await f.arrayBuffer());
    if (bytes.length !== 0x4000) { log("WARN", `Shadow file must be 16384 bytes, got ${bytes.length} bytes.`); return; }
    doShadowRestore(bytes);
  }, [log, doShadowRestore]);

  const doConnect = useCallback(async () => {
    log("INFO", `AUTO CONNECT · ${target.label}`);
    armConnectGuide();
    const res = await run("CONNECT", () => jlinkConnect({ ...opts(), addr: target.base }, onPhase));
    setGuide(null);
    if (!res) { setConnected(false); return; }
    setConnected(true);
    log("SUCCESS", `Connected · core id ${res.coreId} · VTarget ${(res.vtarget_mv / 1000).toFixed(3)} V`);
    if (res.idcodes?.length) log("INFO", `IDCODE: ${res.idcodes.join(", ")}`);
    if (res.memReadable === false)
      log("WARN", "Memory cannot be read although the target is connected — censorship is likely active. READ may be rejected.");
  }, [run, opts, onPhase, log, target]);

  const doDisconnect = useCallback(() => {
    disarmConnectGuide();
    setConnected(false);
    setGuide(null);
    setReadInfo(null);
    setStatus("Terputus");
    log("INFO", "DISCONNECT · JTAG session closed");
  }, [disarmConnectGuide, log]);

  const doRead = useCallback(async () => {
    armConnectGuide();
    log("INFO", `READ ${readRegion.name} · ${readRegion.size} bytes from ${hx(readRegion.base)}`);
    const res = await run("READ", () => jlinkRead({ ...opts(), addr: readRegion.base, size: readRegion.size }, onPhase));
    if (!res) return;
    const bytes = new Uint8Array(res.size);
    for (let i = 0; i < res.size; i++) bytes[i] = parseInt(res.data.substr(i * 2, 2), 16);
    let blank = 0;
    for (let i = 0; i < bytes.length; i++) if (bytes[i] === 0xFF) blank++;
    setDump(bytes);
    setSaveAsReq({ bytes, name: `${target.id}-${readRegion.name}-${hx(readRegion.base)}-${Date.now()}.bin` });
    log("SUCCESS", `Read complete — ${bytes.length} bytes · ${((blank / bytes.length) * 100).toFixed(1)}% blank`);
    if (blank / bytes.length > 0.98) log("WARN", "Almost all 0xFF — flash is blank, or read access is blocked by censorship.");
  }, [run, opts, onPhase, log, readRegion, target]);

  const doReadFull = useCallback(async () => {
    armConnectGuide();
    const regions = target.regions || [{ name: "code-flash", base: target.base, size: target.size }];
    log("INFO", `READ FULL ${regions.length} regions in one session: ${regions.map((r) => `${r.name} ${hx(r.base)}·${r.size}B`).join(" · ")}`);
    const res = await run("READ FULL", () => jlinkReadMulti(opts(), regions, onPhase));
    if (!res) return;
    const stamp = Date.now();
    log("INFO", "Choose a folder to save each read file…");
    for (let i = 0; i < res.regions.length; i++) {
      const rr = res.regions[i];
      const meta = regions[i] || { name: `region${i}` };
      const bytes = new Uint8Array(rr.size);
      for (let k = 0; k < rr.size; k++) bytes[k] = parseInt(rr.data.substr(k * 2, 2), 16);
      let blank = 0;
      for (let k = 0; k < bytes.length; k++) if (bytes[k] === 0xFF) blank++;
      if (meta.name === "code-flash") setDump(bytes);
      log("INFO", `${meta.name} @${hx(rr.addr)}: ${bytes.length} byte · ${((blank / bytes.length) * 100).toFixed(1)}% blank`);
      await saveBinaryWithPicker(bytes, `${target.id}-${meta.name}-${hx(rr.addr)}-${stamp}.bin`, { onLog: log, toast });
      if (meta.name === "shadow-block" && bytes.length > 0x3DE4) {
        const cw = (bytes[0x3DE0] << 24 | bytes[0x3DE1] << 16 | bytes[0x3DE2] << 8 | bytes[0x3DE3]) >>> 0;
        log(cw === 0x55AA55AA ? "SUCCESS" : "WARN", `Censorship word @0x00203DE0 = ${hx(cw)}${cw === 0x55AA55AA ? " → TIDAK tercensor" : " → bukan nilai default 0x55AA55AA"}`);
      }
    }
    log("SUCCESS", "FULL BACKUP complete — all non-volatile regions saved to your computer.");
    log("INFO", "Note: BAM (NXP factory bootloader) resides in internal ROM, not flash — it cannot be backed up or erased.");
  }, [run, opts, onPhase, log, target]);

  // Shadow block berisi censorship word + serial boot password. Salah tulis di sini =
  // JTAG terkunci permanen. Semua operasi tulis ke shadow wajib konfirmasi ketik.
  const shadowGuard = useCallback((region) => {
    if (!region || !/shadow/i.test(region.name)) return true;
    const ok = window.prompt(
      "BAHAYA — SHADOW BLOCK\n\n" +
      "Blok ini berisi censorship word dan serial boot password. Salah tulis di sini membuat JTAG " +
      "TERKUNCI PERMANEN dan ECU hanya bisa dipulihkan lewat serial boot (solder ke kaki chip).\n\n" +
      "Ketik SHADOW untuk melanjutkan:"
    );
    if (ok !== "SHADOW") { log("INFO", "Shadow block operation cancelled (safety guard)."); return false; }
    log("WARN", "Shadow block safety guard bypassed by user confirmation.");
    return true;
  }, [log]);

  const doErase = useCallback(async () => {
    if (!shadowGuard(writeRegion)) return;
    if (!window.confirm(`ERASE CHIP ${target.label}\n\nThe entire flash will be erased. Make sure your dump is saved.\n\nContinue?`)) return;
    log("WRITE", "ERASE CHIP…");
    await askRst("off");
    setGuide("off");
    const res = await run("ERASE", () => jlinkErase({ ...opts(), addr: writeRegion.base }, onPhase));
    setGuide(null);
    if (res) log("SUCCESS", `Erase complete`);
  }, [run, opts, onPhase, log, target, dump, writeRegion, shadowGuard, askRst]);

  const onVerifyFile = useCallback(async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const bytes = new Uint8Array(await f.arrayBuffer());
    setFile({ name: f.name, bytes });
    const hit = regionList.find((r) => r.size === bytes.length);
    const region = hit || writeRegion;
    log("INFO", `VERIFY ${bytes.length} bytes (${f.name}) vs ${region.name} at ${hx(region.base)} (read-only)`);
    const res = await run("VERIFY", () => jlinkVerify({ ...opts(), addr: region.base }, bytes, onPhase));
    if (!res) return;
    if (res.mismatchAt >= 0) log("WARN", `Flash content differs from file starting at offset ${hx(res.mismatchAt)}.`);
    else log("SUCCESS", "Verify OK — flash content identical to file.");
  }, [run, opts, onPhase, log, regionList, writeRegion]);

  // PROGRAM: minta file dulu lewat dialog Open, baru tulis ke region terpilih.
  const programWithFile = useCallback(async (bytes, name, region) => {
    if (!shadowGuard(region)) return false;
    await askRst("off");
    log("WRITE", `PROGRAM ${bytes.length} bytes (${name}) to ${region.name} ${hx(region.base)} + read-back verify`);
    setGuide("off");
    const res = await run(`PROGRAM ${region.name}`, () => jlinkProgram({ ...opts(), addr: region.base, verify: true }, bytes, onPhase));
    setGuide(null);
    if (!res) return false;
    if (res.mismatchAt >= 0) { log("ERROR", `Verify failed at offset ${hx(res.mismatchAt)} — flash content does not match file.`); return false; }
    log("SUCCESS", `${region.name}: PROGRAM + VERIFY OK`);
    return true;
  }, [run, opts, onPhase, log, askRst, shadowGuard]);

  const onProgramFile = useCallback(async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const bytes = new Uint8Array(await f.arrayBuffer());
    setFile({ name: f.name, bytes });
    log("INFO", `File: ${f.name} · ${bytes.length} bytes`);
    const region = writeRegion;
    if (bytes.length !== region.size)
      log("WARN", `File size (${bytes.length} B) does not match region ${region.name} (${region.size} B). Write will still start at ${hx(region.base)}.`);
    if (!window.confirm(`WRITE REGION\n\nFile: ${f.name} (${bytes.length} bytes)\nRegion: ${region.name}\nAddress: ${hx(region.base)} → ${hx(region.base + region.size - 1)}\n\nContinue?`)) { log("INFO", "Write dibatalkan."); return; }
    await programWithFile(bytes, f.name, region);
  }, [log, writeRegion, programWithFile]);

  const doProgram = useCallback(() => {
    programInputRef.current?.click();
  }, []);

  const pickFullFile = useCallback(async (name, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const bytes = new Uint8Array(await f.arrayBuffer());
    const region = regionList.find((r) => r.name === name);
    setFullFiles((p) => ({ ...p, [name]: { name: f.name, bytes, sizeOk: region ? bytes.length === region.size : true } }));
  }, [regionList]);

  // Write Full default HANYA code-flash + data-flash. Shadow block sengaja dikeluarkan —
  // menulis shadow tanpa alasan kuat bisa mengunci JTAG permanen.
  const fullRegions = regionList.filter((r) => !/shadow/i.test(r.name));

  const runWriteFull = useCallback(async () => {
    const chosen = fullRegions.map((r) => ({ region: r, f: fullFiles[r.name] }));
    if (chosen.some((c) => !c.f)) { log("ERROR", `Select ${fullRegions.length} .bin files first.`); return; }
    if (!window.confirm(`WRITE FULL ${fullRegions.length} region:\n` + chosen.map((c) => `• ${c.region.name} @ ${hx(c.region.base)} ← ${c.f.name} (${c.f.bytes.length} B)`).join("\n") + "\n\nLanjut?")) return;
    setFullOpen(false);
    for (const c of chosen) {
      const ok = await programWithFile(c.f.bytes, c.f.name, c.region);
      if (!ok) { log("ERROR", `WRITE FULL stopped at region ${c.region.name}.`); return; }
    }
    log("SUCCESS", "WRITE FULL complete — all regions written and verified.");
  }, [fullRegions, fullFiles, log, programWithFile]);

  const doWriteFull = useCallback(() => {
    setFullOpen(true);
  }, []);

  const kindColor = (k) => k === "ERROR" ? "text-red-300" : k === "SUCCESS" ? "text-emerald-300"
    : k === "WARN" ? "text-amber-300" : k === "WRITE" ? "text-fuchsia-300" : "text-zinc-200";

  return (
    <div className={embedded ? "h-full min-h-0 text-zinc-100" : "min-h-screen bg-[#0a0a0b] text-zinc-100"} data-testid="mpc5602d-page">
      {rstGuide.open && <RstGuide phase={rstGuide.phase} seconds={rstGuide.seconds} />}
      {guide && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[min(92vw,520px)]" data-testid="jlink-connect-guide">
          <div className={`rounded-xl border-2 backdrop-blur-md shadow-2xl shadow-black/60 px-5 py-4 flex items-center gap-4 ${guide === "on" ? "border-emerald-400/60 bg-[#06160f]/95" : "border-amber-400/60 bg-[#1a1206]/95"}`}>
            <Cable className={guide === "on" ? "text-emerald-300" : "text-amber-300"} size={30} />
            <div className="flex-1">
              <div className={`font-black text-[18px] ${guide === "on" ? "text-emerald-300" : "text-amber-300"}`}>
                {guide === "on" ? "ON BUTTON RESET" : guide === "connect" ? "OFF BUTTON RESET" : "BIARKAN BUTTON RESET OFF"}
              </div>
              <div className="text-xs text-zinc-400 mt-0.5">
                {guide === "on"
                  ? "Engine sedang mencoba reset + halt — nyalakan (ON) tombol reset sekarang."
                  : guide === "connect"
                    ? "Posisikan tombol reset OFF — engine sedang mencari koneksi."
                    : "Keep the reset button OFF while the engine searches for a connection."}
              </div>
            </div>
          </div>
        </div>
      )}
      {rstAsk && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="jlink-rst-ask">
          <div className={`w-[min(92vw,520px)] rounded-xl border-2 px-5 py-4 ${rstAsk.mode === "off" ? "border-amber-400/60 bg-[#1a1206]/95" : "border-emerald-400/60 bg-[#06160f]/95"}`}>
            <div className="flex items-center gap-3">
              <Cable className={rstAsk.mode === "off" ? "text-amber-300" : "text-emerald-300"} size={28} />
              <div className={`font-black text-[18px] ${rstAsk.mode === "off" ? "text-amber-300" : "text-emerald-300"}`}>
                {rstAsk.mode === "off" ? "OFF BUTTON RESET" : "ON BUTTON RESET"}
              </div>
            </div>
            <div className="text-xs text-zinc-400 mt-2">
              {rstAsk.mode === "off"
                ? "Make sure the reset button is OFF, then press OK to start writing."
                : "Turn the reset button ON now, then press OK to start reading."}
            </div>
            <div className="mt-4 flex justify-end">
              <button data-testid="jlink-rst-ask-ok"
                onClick={() => {
                  const a = rstAsk; setRstAsk(null);
                  log("INFO", a.mode === "off" ? "Reset button OFF — write started." : "Reset button ON — read started.");
                  a.resolve();
                }}
                className={`h-10 px-6 rounded border font-bold text-[13px] ${rstAsk.mode === "off" ? "border-amber-500/60 bg-amber-500/15 text-amber-300" : "border-emerald-500/60 bg-emerald-500/15 text-emerald-300"}`}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={embedded ? "h-full p-2 sm:p-3 flex flex-col min-h-0" : "max-w-[1200px] mx-auto px-4 sm:px-6 py-8 sm:py-12"}>
        {!embedded && (
          <>
            <div className="flex items-center gap-3 mb-2">
              <Cpu className="text-cyan-400" size={28} />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">MPC5602D FLASH</h1>
            </div>
            <p className="text-sm text-zinc-300 mb-8">Read, erase, and program MPC56xx via the local bridge</p>
          </>
        )}
        <div className={embedded ? "flex-1 min-h-0 rounded-xl border border-cyan-900/50 bg-[#0a0f16] overflow-hidden flex flex-col" : ""}>
          {embedded && (
            <div className="flex items-center gap-2 px-4 py-3 border-b border-cyan-900/40 bg-[#0c1420] shrink-0">
              <Cpu className="h-4 w-4 text-cyan-400" />
              <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-bold">MPC56xx Flash Tool</span>
              <span className="ml-auto flex items-center gap-1.5 text-[11px] font-mono">
                <span className={`inline-block h-2 w-2 rounded-full ${connected ? "bg-emerald-400" : "bg-slate-600"}`} data-testid="jlink-conn-dot" />
                {connected ? "Online" : "Offline"}
              </span>
            </div>
          )}
          {IS_ANDROID_APP && (
            <div className="mx-3 mt-3 rounded-lg border border-amber-500/40 bg-[#1a1206] px-4 py-3 text-[12px] text-amber-200" data-testid="jlink-android-notice">
              <div className="font-black uppercase tracking-wide text-amber-300 mb-1">MPC56xx JTAG tidak tersedia di Android</div>
              Tool ini butuh SEGGER J-Link + TRMD JTAG engine yang hanya berjalan di Windows. Gunakan <b>TRMD Performance (Windows EXE)</b> untuk MPC5602D/5604B/5606B.
              Di Android, tab <b>R8C/M16C</b> dan <b>RH850</b> (FTDI TTL via USB-OTG) tetap bisa dipakai.
            </div>
          )}
          <div className={`grid lg:grid-cols-[380px_1fr] gap-4 sm:gap-6 items-start ${embedded ? "p-3 sm:p-4 flex-1 min-h-0" : ""}`}>
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-[#20242c] bg-[#101319] p-3 space-y-2">
              <label className="block text-xs text-zinc-300">TARGET MCU</label>
              <select value={tIdx} onChange={(e) => setTIdx(Number(e.target.value))} disabled={busy}
                data-testid="jlink-target"
                className="w-full h-10 rounded-[5px] border border-[#262c36] bg-[#0b0d12] px-2 text-sm">
                {JLINK_TARGETS.map((t, i) => <option key={t.id} value={i}>{t.label}</option>)}
              </select>

              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <div>
                  <div className="text-xs text-zinc-300">ATTACH MODE</div>
                  <div className="text-zinc-200" data-testid="jlink-auto-mode">AUTO</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-300">FLASH AREA</div>
                  <div className="text-zinc-300">{hx(target.base)} · {target.size} B</div>
                </div>
              </div>
            </div>

            <div className="flex-1 rounded-lg border border-[#20242c] bg-[#101319] p-3 space-y-2" data-testid="jlink-area-read">
              <div className="flex items-center gap-2">
                <HardDriveDownload size={15} className="text-zinc-400" />
                <div className="text-[12px] font-bold uppercase tracking-[0.12em] text-zinc-300">AREA READ / WRITE</div>
              </div>
              <div>
                <select value={readRegionKey} onChange={(e) => { setReadRegionKey(e.target.value); setWriteRegionKey(e.target.value); }} disabled={busy}
                  data-testid="jlink-region-read"
                  className="w-full h-10 rounded-[5px] border border-[#262c36] bg-[#0b0d12] px-2 text-sm">
                  {regionList.map((r) => <option key={r.name} value={r.name}>{regionLabel(r)}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={connected ? doDisconnect : doConnect} disabled={busy || IS_ANDROID_APP} data-testid="jlink-connect"
                  title={IS_ANDROID_APP ? "MPC56xx JTAG hanya tersedia di Windows EXE" : undefined}
                  className={BTN_ACCENT}>
                  <Plug size={15} /> {connected ? "Disconnect" : "Connect"}
                </button>
                <button onClick={doRead} disabled={busy} data-testid="jlink-read"
                  className={BTN_NEUTRAL}>
                  <HardDriveDownload size={15} /> Read
                </button>
                <button onClick={doProgram} disabled={busy} data-testid="jlink-program"
                  className={BTN_DANGER}>
                  <Upload size={15} /> Write
                </button>
                <label className={`${BTN_NEUTRAL} cursor-pointer`} data-testid="jlink-verify-file-label">
                  <ScanSearch size={15} /> Verify
                  <input type="file" accept=".bin,.hex,application/octet-stream" className="hidden" onChange={onVerifyFile} disabled={busy} data-testid="jlink-file" />
                </label>
                <button onClick={doErase} disabled={busy} data-testid="jlink-erase"
                  className={BTN_DANGER}>
                  <Eraser size={15} /> Erase Chip
                </button>
              </div>
              <input ref={programInputRef} type="file" accept=".bin,.hex,application/octet-stream" className="hidden" onChange={onProgramFile} data-testid="jlink-program-file" />
            </div>

            {fullOpen && (
              <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="jlink-writefull-dialog">
                <div className="w-full max-w-md rounded-lg border border-[#2a2f39] bg-[#0c0e14] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Upload size={16} className="text-zinc-400" />
                    <div className="font-bold uppercase tracking-[0.12em] text-[13px] text-zinc-200">WRITE FULL — select {fullRegions.length} files</div>
                  </div>
                  <div className="space-y-2">
                    {fullRegions.map((r) => {
                      const sel = fullFiles[r.name];
                      return (
                        <div key={r.name} className="rounded-[5px] border border-[#20242c] bg-[#0b0d12] p-2">
                          <div className="text-[11px] text-zinc-400 mb-1">{r.name} · {hx(r.base)} · {r.size} B</div>
                          <label className="h-9 w-full rounded-[5px] border border-[#262c36] bg-[#161a21] hover:bg-[#1d2027] text-zinc-300 inline-flex items-center justify-center gap-2 text-[12px] cursor-pointer">
                            {sel ? `${sel.name} (${sel.bytes.length} B)` : "Select .bin file"}
                            <input type="file" accept=".bin,.hex,application/octet-stream" className="hidden"
                              onChange={(e) => pickFullFile(r.name, e)} data-testid={`jlink-full-file-${r.name}`} />
                          </label>
                          {sel && !sel.sizeOk && <div className="text-[11px] text-amber-400 mt-1">File size does not match this region.</div>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 flex items-center justify-end gap-2">
                    <button onClick={() => setFullOpen(false)} data-testid="jlink-writefull-cancel"
                      className="h-9 px-4 rounded-[5px] border border-[#262c36] bg-[#161a21] hover:bg-[#1d2027] text-zinc-300 text-[12px] font-semibold">Cancel</button>
                    <button onClick={runWriteFull} disabled={fullRegions.some((r) => !fullFiles[r.name])} data-testid="jlink-writefull-start"
                      className="h-9 px-4 rounded-[5px] border border-[#5a2a24] bg-[#20110f] hover:bg-[#2a1613] text-[#d98b7f] text-[12px] font-semibold disabled:opacity-35">
                      Start Write Full
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="relative flex flex-col min-h-0 h-full rounded-lg border border-slate-800 bg-[#080b11] overflow-hidden">
            {false && readInfo && haltToast && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 w-[min(92%,380px)] rounded-lg border border-sky-400/50 bg-[#061019]/95 backdrop-blur-md shadow-2xl shadow-black/60 px-3 py-2 animate-in fade-in zoom-in-95 duration-200" data-testid="jlink-read-progress">
                <div className="flex items-center gap-2">
                  <Cpu className={readInfo.done ? (readInfo.ok ? "text-emerald-400" : "text-red-400") : "text-sky-300"} size={16} />
                  <div className="flex-1 min-w-0">
                    <div className={`font-bold text-[11px] ${readInfo.done ? (readInfo.ok ? "text-emerald-300" : "text-red-300") : "text-sky-300"}`}>
                      {readInfo.done
                        ? readInfo.result
                        : readInfo.halted
                          ? `TARGET HALTED — ${readInfo.mode === "tulis" ? "WRITING FLASH" : "READING MEMORY"}`
                          : `${readInfo.job || "PROCESS"} — CONNECTING TO TARGET…`}
                    </div>
                    <div className="text-[10px] text-zinc-400 font-mono truncate">{readInfo.addr}</div>
                  </div>
                  <span className="text-sm font-mono font-bold text-zinc-100 tabular-nums">{readInfo.pct}%</span>
                </div>
                <div className="h-1.5 w-full rounded bg-[#132231] mt-1.5 overflow-hidden">
                  <div className={`h-full transition-all ${readInfo.done ? (readInfo.ok ? "bg-emerald-400" : "bg-red-400") : "bg-sky-400"}`} style={{ width: `${readInfo.pct}%` }} />
                </div>
                {readInfo.done && (
                  <button onClick={() => { setHaltToast(false); setReadInfo(null); }} data-testid="jlink-progress-close"
                    className="mt-2 w-full h-7 rounded-[4px] border border-[#2a313c] bg-[#161b22] hover:bg-[#1d232c] text-[11px] font-semibold tracking-wide text-zinc-200">
                    CLOSE
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-800 bg-[#0c1017] shrink-0">
              <Cpu className="h-4 w-4 text-cyan-500" />
              <span className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">MPC56xx Console</span>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-[10px] font-semibold text-slate-300 tabular-nums" data-testid="jlink-status">{status}</span>
                <button onClick={copyLog} disabled={!lines.length} data-testid="jlink-copy-log"
                  className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border transition-colors disabled:opacity-40 ${copied ? "border-emerald-700 text-emerald-400" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
                  <ClipboardCopy className="h-3 w-3" /> {copied ? "Copied" : "Copy"}
                </button>
                <button onClick={() => setLines([])} disabled={!lines.length} data-testid="jlink-clear-log"
                  className="text-[10px] px-2 py-0.5 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors disabled:opacity-40">
                  Clear
                </button>
              </div>
            </div>
            <div className="relative h-5 w-full tt-bar-track shrink-0 overflow-hidden" data-testid="jlink-progress">
              <div className="h-full tt-bar-fill transition-all" style={{ width: `${progress}%` }} />
              {busy && progress > 0 && <span data-testid="jlink-progress-text" className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-white drop-shadow">{progress}%</span>}
            </div>
            <div ref={logRef} className="p-4 overflow-y-auto font-mono text-xs leading-relaxed space-y-1 break-all flex-1 min-h-[300px] lg:min-h-0" data-testid="jlink-log">
              {lines.length === 0 && <div className="text-zinc-400">No activity yet.</div>}
              {lines.map((l, i) => (
                <div key={i} className={kindColor(l.kind)}>
                  <span className="text-zinc-400">[{l.t}]</span> {l.msg}
                </div>
              ))}
            </div>
          </div>
        </div>
        </div>
      </div>
    {saveAsReq && (
      <SaveAsDialog open title="Save Read Result" defaultName={saveAsReq.name}
        onCancel={() => setSaveAsReq(null)}
        onSave={async (finalName) => { const req = saveAsReq; setSaveAsReq(null); const ok = await saveBinaryWithPicker(req.bytes, finalName, { onLog: log, toast }); if (ok) log("SUCCESS", `Saved: ${finalName}`); }} />
    )}
    </div>
  );
}
